import React, { useState, useEffect, useCallback, useRef } from 'react';
import { useNavigate } from 'react-router-dom';
import { gamesAPI } from '../../services/api';
import { useProfile } from '../../context/ProfileContext';

const CARD_EMOJIS = ['🐶','🐱','🦊','🐸','🦄','🐙','🦋','🐬','🌈','⭐','🍕','🎸'];

function shuffle(arr) {
  const a = [...arr];
  for (let i = a.length - 1; i > 0; i--) {
    const j = Math.floor(Math.random() * (i + 1));
    [a[i], a[j]] = [a[j], a[i]];
  }
  return a;
}

function createCards(count = 8) {
  const emojis = CARD_EMOJIS.slice(0, count);
  return shuffle([...emojis, ...emojis].map((emoji, i) => ({
    id: i,
    emoji,
    flipped: false,
    matched: false
  })));
}

export default function MemoryGameScreen() {
  const navigate = useNavigate();
  const { profile, refreshProfile } = useProfile();
  const [cards, setCards] = useState([]);
  const [selected, setSelected] = useState([]);
  const [moves, setMoves] = useState(0);
  const [matches, setMatches] = useState(0);
  const [gameOver, setGameOver] = useState(false);
  const [startTime, setStartTime] = useState(null);
  const [elapsed, setElapsed] = useState(0);
  const [pointsEarned, setPointsEarned] = useState(null);
  const timerRef = useRef(null);

  const TOTAL_PAIRS = 8;

  const initGame = useCallback(() => {
    setCards(createCards(TOTAL_PAIRS));
    setSelected([]);
    setMoves(0);
    setMatches(0);
    setGameOver(false);
    setElapsed(0);
    setPointsEarned(null);
    const now = Date.now();
    setStartTime(now);
    timerRef.current = setInterval(() => {
      setElapsed(Math.floor((Date.now() - now) / 1000));
    }, 1000);
  }, []);

  useEffect(() => {
    initGame();
    return () => clearInterval(timerRef.current);
  }, [initGame]);

  const handleCard = useCallback((card) => {
    if (card.flipped || card.matched || selected.length === 2) return;

    const newSelected = [...selected, card];
    setCards(prev => prev.map(c => c.id === card.id ? { ...c, flipped: true } : c));
    setSelected(newSelected);

    if (newSelected.length === 2) {
      setMoves(m => m + 1);
      const [a, b] = newSelected;

      if (a.emoji === b.emoji) {
        // Match!
        setTimeout(() => {
          setCards(prev => prev.map(c =>
            c.emoji === a.emoji ? { ...c, matched: true } : c
          ));
          setSelected([]);
          const newMatches = matches + 1;
          setMatches(newMatches);

          if (newMatches === TOTAL_PAIRS) {
            // Game complete!
            clearInterval(timerRef.current);
            setGameOver(true);
            saveResult(moves + 1, Math.floor((Date.now() - startTime) / 1000));
          }
        }, 600);
      } else {
        // No match
        setTimeout(() => {
          setCards(prev => prev.map(c =>
            (c.id === a.id || c.id === b.id) ? { ...c, flipped: false } : c
          ));
          setSelected([]);
        }, 900);
      }
    }
  }, [selected, matches, moves, startTime]);

  const saveResult = async (totalMoves, duration) => {
    if (!profile) return;
    try {
      const score = Math.max(0, 1000 - (totalMoves * 20) - (duration * 2));
      const result = await gamesAPI.saveResult({
        profileId: profile.id,
        gameId: 'memory-game',
        score,
        durationSeconds: duration,
        metadata: { moves: totalMoves, pairs: TOTAL_PAIRS }
      });
      setPointsEarned(result.pointsEarned);
      refreshProfile();
    } catch (e) {
      console.error('Failed to save:', e);
    }
  };

  const score = Math.max(0, 1000 - (moves * 20) - (elapsed * 2));

  return (
    <div className="screen">
      <div className="screen-header" style={{ background: 'linear-gradient(135deg, #667eea, #764ba2)', paddingBottom: 20 }}>
        <button className="btn-back" onClick={() => navigate('/games')} style={{ background: 'rgba(255,255,255,0.2)', color: 'white' }}>←</button>
        <div style={{ flex: 1 }}>
          <h1 style={{ fontFamily: 'var(--font-title)', color: 'white', fontSize: '1.4rem' }}>🧠 Memory Game</h1>
          <div style={{ display: 'flex', gap: 16, color: 'rgba(255,255,255,0.85)', fontSize: '0.85rem', marginTop: 2 }}>
            <span>Moves: {moves}</span>
            <span>⏱ {elapsed}s</span>
            <span>Score: {score}</span>
          </div>
        </div>
      </div>

      <div className="screen-body" style={{ paddingTop: 20 }}>
        {/* Progress */}
        <div style={{ marginBottom: 16 }}>
          <div style={{ display: 'flex', justifyContent: 'space-between', fontSize: '0.85rem', fontWeight: 700, marginBottom: 6 }}>
            <span>Pairs found</span>
            <span>{matches}/{TOTAL_PAIRS}</span>
          </div>
          <div className="progress-bar">
            <div className="progress-bar-fill" style={{ width: `${(matches / TOTAL_PAIRS) * 100}%` }} />
          </div>
        </div>

        {/* Card Grid */}
        <div style={{ display: 'grid', gridTemplateColumns: 'repeat(4, 1fr)', gap: 10 }}>
          {cards.map(card => (
            <button
              key={card.id}
              onClick={() => handleCard(card)}
              style={{
                aspectRatio: '1',
                borderRadius: 14,
                border: 'none',
                cursor: card.matched ? 'default' : 'pointer',
                fontSize: '1.8rem',
                transition: 'all 0.3s',
                background: card.matched
                  ? '#E8F5E9'
                  : card.flipped
                  ? 'white'
                  : 'linear-gradient(135deg, #667eea, #764ba2)',
                boxShadow: card.matched
                  ? '0 0 0 3px var(--green)'
                  : '0 4px 12px rgba(0,0,0,0.15)',
                transform: card.flipped || card.matched ? 'scale(1)' : 'scale(0.97)',
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center'
              }}
            >
              {card.flipped || card.matched ? card.emoji : '❓'}
            </button>
          ))}
        </div>

        {/* Game Over Overlay */}
        {gameOver && (
          <div style={{
            position: 'fixed', inset: 0, background: 'rgba(0,0,0,0.7)',
            display: 'flex', alignItems: 'center', justifyContent: 'center',
            zIndex: 100, padding: 20
          }}>
            <div className="card" style={{ textAlign: 'center', maxWidth: 320, width: '100%' }}>
              <div style={{ fontSize: '4rem', marginBottom: 8 }}>🎉</div>
              <h2 style={{ fontFamily: 'var(--font-title)', fontSize: '1.8rem', color: 'var(--purple)' }}>You Won!</h2>
              <p style={{ color: 'var(--gray)', margin: '8px 0 16px' }}>
                Finished in {moves} moves and {elapsed} seconds!
              </p>
              <div style={{ background: 'var(--bg)', borderRadius: 16, padding: 12, marginBottom: 16 }}>
                <div style={{ fontFamily: 'var(--font-title)', fontSize: '2rem', color: 'var(--orange)' }}>⭐ {score}</div>
                <div style={{ color: 'var(--gray)', fontSize: '0.85rem' }}>Final Score</div>
              </div>
              {pointsEarned && (
                <div style={{ background: '#FFF9E6', border: '2px solid var(--yellow)', borderRadius: 12, padding: 10, marginBottom: 16 }}>
                  <strong>+{pointsEarned} points earned! 🌟</strong>
                </div>
              )}
              <div style={{ display: 'flex', gap: 10 }}>
                <button className="btn btn-primary" style={{ flex: 1 }} onClick={initGame}>Play Again</button>
                <button className="btn" style={{ flex: 1, background: 'var(--bg)' }} onClick={() => navigate('/games')}>Games</button>
              </div>
            </div>
          </div>
        )}
      </div>
    </div>
  );
}
